<!--
  CompanyForm -- the ONE Company form: business identity (display name, time
  zone, tax ID), billing contact, billing address and ship-from address.

  Extracted from DistributorAccountInfo.vue so the Owner's Company page and
  the SuperAdmin distributor edit page mount the SAME fields, labels and
  validation (docs/planning/2026-10-19-distributor-setup-ia-proposal.md, 3.3).

  REQUIRED FIELDS: every field the launch gate needs is marked `*`: display
  name, billing contact name/email/phone, and street, city, state and ZIP of
  BOTH addresses (api/src/services/launch_gates.py _COMPANY_REQUIRED_GROUPS,
  the dashboard's "Company details complete" row, which stays the source of
  truth). The marks and the "needed to launch a store" note are INFORMATION:
  a blank gate field does NOT block the auto-save, so the form can be filled
  one field at a time and nothing is lost (partial saves, Jake 2026-10-23).
  Under the status, `company-incomplete-summary` (an info alert) names what is
  still needed ("Saved. Still needed to launch a store: Billing contact
  (phone); ...") and disappears once all twelve are filled. The form never
  says the Company is complete; only the dashboard row does.
  The one blank that still blocks is the display name (the API refuses to
  clear it). CompanyForm.required.spec.js pins the lists and the behavior.

  AUTO-SAVES like the store editor (Bug Intake #418, Jake 2026-10-23): there
  is no Save button. An edit arms a debounce; one save sends the whole record
  (the time zone travels with the billing city, so a rejected value leaves
  nothing half-written) and the status line reads "All changes saved". The
  FORMAT rules run BEFORE every save: a record that fails them (a bad ZIP, a
  malformed email, an empty name) is not sent and the status says it was not
  saved. The server re-checks (a bad ZIP is a 400), and the form never says
  "saved" for a request that failed. The safeguards come from
  composables/useRecordAutosave (debounce, a stop after 5 identical saves with
  no person typing, trusted input only, a flush when the page goes away, and a
  beforeunload prompt while an edit is unsaved or refused).

  The tax ID (EIN) is the one other write: the API never returns it, so it
  applies as soon as it is confirmed, through its own endpoint, and reports
  at the tax ID control.

  Props
    - distributor      the record being edited (read here; never mutated).
    - save             async (record) => { ok: true } or { ok: false, error }.
                       It persists the record (it owns the transport and the
                       wording of the error, which must carry no field
                       values); a throw is treated as a failure.
    - einDistributorId id the tax ID endpoints act on; null hides the tax ID.
    - readonly         every input disabled, no autosave, no Copy, no tax ID
                       control (a caller without edit rights).

  Emits
    - update:field     ({ key, value }) -- the host assigns it. The record is
                       never mutated here. The time zone's "not set" option
                       is emitted as null, never "".
    - saved            after each good save.
    - save-error       the message shown, after a refused save.

  Public: validate() resolves true when the field rules pass; flush() saves
  a pending edit now (resolves true when everything is saved).

  Never logs field values (this is the tax ID and tenant-data write path).
-->
<template>
  <v-form ref="form">
    <p
      v-if="!readonly"
      class="text-medium-emphasis mb-2"
      data-testid="company-required-note"
    >
      Fields marked * are needed to launch a store. ThreadFlow checks them
      before any store goes live.
    </p>
    <!--
      The save status, always visible at the top (the same words as the store
      editor's save indicator). A refused or unsent save is spelled out below
      it: never "All changes saved" for a record that was not.
    -->
    <div v-if="!readonly" class="company-save-status mb-2">
      <p
        class="d-flex align-center ga-2 mb-0"
        :data-state="autosave.status.value"
        data-testid="company-save-status"
      >
        <span class="company-save-dot" :style="{ background: statusDot }"></span>
        <span>{{ statusLabel }}</span>
      </p>
      <v-alert
        v-if="statusMessage"
        type="error"
        variant="tonal"
        density="comfortable"
        class="mt-2"
        data-testid="company-save-error"
      >
        {{ statusMessage }}
      </v-alert>
    </div>

    <v-alert
      v-if="incompleteSummary"
      type="info"
      variant="tonal"
      class="mb-4"
      data-testid="company-incomplete-summary"
    >
      {{ incompleteSummary }}
    </v-alert>

    <h2 class="md-header mt-2">Business</h2>
    <v-text-field
      :model-value="distributor.name"
      label="Display name *"
      hint="Shown on your stores."
      :rules="nameRules"
      :disabled="readonly"
      data-testid="company-name"
      @update:model-value="setField('name', $event)"
    ></v-text-field>
    <v-select
      :model-value="distributor.timezone || ''"
      :items="timezoneItems"
      item-title="label"
      item-value="value"
      label="Time zone"
      hint="Sets where &quot;today&quot; starts on your dashboard."
      persistent-hint
      :disabled="readonly"
      data-testid="company-timezone"
      @update:model-value="setField('timezone', $event === '' ? null : $event)"
    ></v-select>

    <!--
      EIN: write-only. The API never returns it; this shows the last four and
      applies a Replace/Remove as soon as it is confirmed.
    -->
    <template v-if="einDistributorId != null && !readonly">
      <h2 class="md-header mt-4">Tax ID (EIN)</h2>
      <p class="mb-2" data-testid="company-ein-purpose">
        Your business's federal tax ID.
      </p>
      <company-ein-field
        ref="einField"
        :distributor-id="einDistributorId"
        mode="immediate"
      />
    </template>

    <!--
      BILLING CONTACT (Jake 2026-10-19): who ThreadFlow bills and writes to.
      NOT the account owner's login (that lives under Team & Permissions): the
      name and phone were copied from the owner once at sign-up and are
      independent now. The email is the same distributors.email column this
      page always had, relabeled.
    -->
    <h2 class="md-header mt-4">Billing contact</h2>
    <p class="mb-2" data-testid="company-billing-contact-purpose">
      Who ThreadFlow sends invoices and billing notices to. This is separate
      from the account owner's login.
    </p>
    <v-text-field
      :model-value="distributor.billing_contact_name"
      :label="requiredLabel('Billing contact name')"
      maxlength="255"
      :disabled="readonly"
      data-testid="company-billing-contact-name"
      @update:model-value="setField('billing_contact_name', $event)"
    ></v-text-field>
    <v-text-field
      :model-value="distributor.email"
      :label="requiredLabel('Billing contact email')"
      hint="Invoices and billing notices are sent here."
      :rules="[optionalEmail]"
      :disabled="readonly"
      data-testid="company-billing-contact-email"
      @update:model-value="setField('email', $event)"
    ></v-text-field>
    <v-text-field
      :model-value="distributor.billing_contact_phone"
      :label="requiredLabel('Billing contact phone')"
      hint="Not the shipping phone below."
      maxlength="50"
      :disabled="readonly"
      data-testid="company-billing-contact-phone"
      @update:model-value="setField('billing_contact_phone', $event)"
    ></v-text-field>

    <h2 class="md-header mt-4">Billing address</h2>
    <p class="mb-2" data-testid="company-billing-address-purpose">
      Where your ThreadFlow invoices are addressed. It is separate from the
      address your orders ship from.
    </p>
    <company-address-fields
      :distributor="distributor"
      field-prefix="billing_address_"
      :field-attrs="billingAddressAttrs"
      :disabled="readonly"
      :required="requireGateFields"
      @update:field="onAddressField"
    />
    <div v-if="!readonly" class="d-flex align-center ga-4 mb-4">
      <v-btn
        size="small"
        variant="outlined"
        data-testid="company-billing-copy-to-shipping"
        @click="copyBillingToShipping"
      >
        Copy to shipping address
      </v-btn>
      <span
        class="text-medium-emphasis"
        data-testid="company-billing-copy-explainer"
      >
        Fills the shipping address below with this address.
      </span>
    </div>

    <!--
      STRUCTURED physical address (Jake 2026-09-30). Sent to the decorator as
      the SENDER on every shipment (DecoNetwork shipping.ship_from), but only
      once street, city, state and ZIP are all set; until then the decorator's
      own address is used. The free-text address it replaces is legacy: shown
      read-only below as a hint for re-entry, never edited or parsed.
      maxlength mirrors the column / contract limits.
    -->
    <h2 class="md-header mt-4">Shipping address</h2>
    <p class="mb-2" data-testid="company-ship-from-explainer">
      This is the address your shoppers' orders ship from. It is used as the
      sender address on shipping labels. Shipping rates and shipping charges
      are based on your decorator's origin address, not this one.
    </p>
    <p class="text-medium-emphasis mb-2" data-testid="company-ship-from-summary">
      {{ shipFromSummary }}
    </p>
    <p
      v-if="distributor.address && !distributor.address_line1"
      class="text-medium-emphasis mb-2"
      data-testid="company-legacy-address"
    >
      Previously entered as: {{ distributor.address }}
    </p>
    <company-address-fields
      :distributor="distributor"
      field-prefix="address_"
      :field-attrs="shipFromAttrs"
      :disabled="readonly"
      :required="requireGateFields"
      with-phone
      @update:field="onAddressField"
    />

  </v-form>
</template>

<script>
import { getCurrentInstance, onBeforeUnmount, ref } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import axios from "@/axios";
import rules from "@/mixins/rules";
import { useRecordAutosave } from "@/composables/useRecordAutosave";
import { COMPANY_FIELDS } from "./companyFields";
import { COMMON_TIMEZONES } from "@/constants/timezones";
import CompanyAddressFields from "./CompanyAddressFields.vue";
import CompanyEinField from "./CompanyEinField.vue";

// data-testid for each address input, as literals (see CompanyAddressFields).
const BILLING_ADDRESS_ATTRS = {
  line1: { "data-testid": "company-billing-address-line1" },
  line2: { "data-testid": "company-billing-address-line2" },
  city: { "data-testid": "company-billing-address-city" },
  state: { "data-testid": "company-billing-address-state" },
  postal_code: { "data-testid": "company-billing-address-postal-code" },
  country: { "data-testid": "company-billing-address-country" },
  check: { "data-testid": "company-billing-address-check" },
};
const SHIP_FROM_ATTRS = {
  line1: { "data-testid": "company-address-line1" },
  line2: { "data-testid": "company-address-line2" },
  city: { "data-testid": "company-address-city" },
  state: { "data-testid": "company-address-state" },
  postal_code: { "data-testid": "company-address-postal-code" },
  country: { "data-testid": "company-address-country" },
  phone: { "data-testid": "company-address-phone" },
  check: { "data-testid": "company-address-check" },
};
// THE one switch that marks the launch gate's Company fields in this form
// (the `*` labels, the note and the "still needed" summary all read it). Set
// it to false to drop all of that; the dashboard's
// "Company details complete" row (launch_gates.py) is unaffected either way.
const REQUIRE_GATE_FIELDS = true;

// The required fields by section, in form order, with the plain-language name
// the summary uses. Mirrors launch_gates.py _COMPANY_REQUIRED_GROUPS.
const REQUIRED_SECTIONS = [
  { title: "Business", fields: [["name", "display name"]] },
  {
    title: "Billing contact",
    fields: [
      ["billing_contact_name", "name"],
      ["email", "email"],
      ["billing_contact_phone", "phone"],
    ],
  },
  {
    title: "Billing address",
    fields: [
      ["billing_address_line1", "street address"],
      ["billing_address_city", "city"],
      ["billing_address_state", "state"],
      ["billing_address_postal_code", "ZIP code"],
    ],
  },
  {
    title: "Shipping address",
    fields: [
      ["address_line1", "street address"],
      ["address_city", "city"],
      ["address_state", "state"],
      ["address_postal_code", "ZIP code"],
    ],
  },
];
const ADDRESS_PARTS = ["line1", "line2", "city", "state", "postal_code", "country"];

export default {
  name: "CompanyForm",
  components: { CompanyAddressFields, CompanyEinField },
  mixins: [rules],
  props: {
    distributor: { type: Object, required: true },
    save: { type: Function, default: null },
    einDistributorId: { type: [Number, String], default: null },
    readonly: { type: Boolean, default: false },
  },
  emits: ["update:field", "saved", "save-error"],
  setup(props, { emit }) {
    const instance = getCurrentInstance();
    // A save went through this session (gates the summary's "Saved." lead).
    const savedOnce = ref(false);
    const autosave = useRecordAutosave({
      save: async () => {
        if (typeof props.save !== "function") return { ok: false };
        const result = await props.save(props.distributor);
        if (result && result.ok) {
          savedOnce.value = true;
          emit("saved");
        } else {
          emit("save-error", (result && result.error) || "Could not save your company details.");
        }
        return result;
      },
      validate: () => instance.proxy.validate(),
      // What would be sent now. No secrets: the tax ID is not in the record.
      signature: () =>
        JSON.stringify(COMPANY_FIELDS.map((key) => props.distributor[key] ?? null)),
    });

    // Leaving the page (a link, the back button) saves a pending edit first;
    // if it cannot be saved the person decides, so an edit is never dropped
    // silently. Only registered under a real route (a bare mount has none).
    if (instance?.proxy?.$route?.matched?.length) {
      onBeforeRouteLeave(async () => {
        if (props.readonly) return true;
        if (await autosave.flush()) return true;
        return window.confirm(
          "Some of your changes were not saved. Leave this page anyway?"
        );
      });
    }
    // The form going away for any other reason still saves what is pending.
    onBeforeUnmount(() => {
      if (!props.readonly) autosave.flush();
      autosave.dispose();
    });

    return { autosave, savedOnce };
  },
  data() {
    return {
      // The platform-wide default, only to name it in the "not set" option.
      platformDefaultTz: null,
      requireGateFields: REQUIRE_GATE_FIELDS,
      billingAddressAttrs: BILLING_ADDRESS_ATTRS,
      shipFromAttrs: SHIP_FROM_ATTRS,
    };
  },
  computed: {
    // The display name is the one required field that still blocks a save:
    // the API refuses to clear it (a company always has a name). Every other
    // `*` field is informational (see incompleteSummary).
    nameRules() {
      return this.requireGateFields ? [this.validateNotSpaces] : [];
    },

    // Computed live from the record, so it names exactly what is still blank
    // and disappears the moment the last field is filled. "Saved." is said
    // only once a save went through and nothing is pending after it.
    // "Saved. Still needed to launch a store: Billing contact (phone);
    // Billing address (street address, city)." or "".
    incompleteSummary() {
      if (this.readonly) return "";
      const sections = this.findIncomplete();
      if (!sections.length) return "";
      const parts = sections.map((s) => `${s.title} (${s.names.join(", ")})`);
      const saved = this.savedOnce && this.autosave.status.value === "saved";
      return `${saved ? "Saved. " : ""}Still needed to launch a store: ${parts.join("; ")}.`;
    },

    // "" is the select's not-set option; setField turns it into null so the
    // API never receives an empty string.
    timezoneItems() {
      const raw = this.platformDefaultTz;
      const match = COMMON_TIMEZONES.find((tz) => tz.value === raw);
      const name = typeof raw === "string" && raw !== "" ? (match ? match.label : raw) : "UTC";
      return [
        { value: "", label: `Not set (uses the platform default: ${name})` },
        ...COMMON_TIMEZONES,
      ];
    },

    // Mirrors the backend rule in api/src/integrations/deconetwork/
    // serializers.py (_SHIP_FROM_REQUIRED, used by _ship_from_block): the
    // address is sent as the shipment's sender only when these four are all
    // present. Keep this list in step with _SHIP_FROM_REQUIRED. Reads the
    // form, so it also updates while typing.
    statusLabel() {
      switch (this.autosave.status.value) {
        case "saving":
          return "Saving...";
        case "dirty":
          return "Unsaved changes";
        case "invalid":
          return "Not saved";
        case "error":
          return "Save failed";
        default:
          return "All changes saved";
      }
    },
    statusDot() {
      switch (this.autosave.status.value) {
        case "saving":
        case "dirty":
          return "#FDD835";
        case "invalid":
        case "error":
          return "#ef0405";
        default:
          return "#00d084";
      }
    },
    // The reason for an unsent or refused save; "" when there is none.
    statusMessage() {
      const state = this.autosave.status.value;
      return state === "invalid" || state === "error" ? this.autosave.message.value : "";
    },

    shipFromSummary() {
      const part = (key) => String(this.distributor[key] || "").trim();
      const required = ["address_line1", "address_city", "address_state", "address_postal_code"];
      if (required.some((key) => !part(key))) {
        return "Incomplete: shipments use the decorator's address until street, city, state and ZIP are set.";
      }
      const street = [part("address_line1"), part("address_line2"), part("address_city")]
        .filter(Boolean)
        .join(", ");
      return `Shipments are sent from: ${street}, ${part("address_state")} ${part("address_postal_code")}`;
    },
  },
  mounted() {
    this.loadPlatformDefault();
  },
  methods: {
    // Every edit by the person goes through here (or onAddressField): the
    // record changed, so a save is due. Server responses never come through
    // here, which is what keeps a save from arming the next one.
    setField(key, value) {
      this.$emit("update:field", { key, value });
      if (!this.readonly) this.autosave.markDirty();
    },
    onAddressField(payload) {
      this.$emit("update:field", payload);
      if (!this.readonly) this.autosave.markDirty();
    },

    // Best-effort and cosmetic (it only names the platform default in the
    // "not set" option). A failure leaves the label reading "UTC", which is
    // the resolver's last resort.
    async loadPlatformDefault() {
      try {
        const resp = await axios.get("/api/platform-settings/default-timezone");
        const v = resp?.data?.value;
        this.platformDefaultTz = typeof v === "string" ? v : null;
      } catch (e) {
        this.platformDefaultTz = null;
      }
    },

    // Purely client-side: fills the ship-from fields from the billing ones.
    // Each field goes through setField, so the auto-save follows.
    copyBillingToShipping() {
      for (const part of ADDRESS_PARTS) {
        this.setField(`address_${part}`, this.distributor[`billing_address_${part}`]);
      }
    },

    requiredLabel(label) {
      return this.requireGateFields ? `${label} *` : label;
    },

    // The required fields that are blank right now, grouped by section.
    findIncomplete() {
      if (!this.requireGateFields) return [];
      const blank = (key) => !String(this.distributor[key] || "").trim();
      return REQUIRED_SECTIONS.map((section) => ({
        title: section.title,
        names: section.fields.filter(([key]) => blank(key)).map(([, name]) => name),
      })).filter((section) => section.names.length > 0);
    },

    // Bring the first problem into view (the form can be long and the blank
    // field may be far from the status line). Best effort: no-op where
    // unsupported.
    scrollToFirstError() {
      const el = this.$el?.querySelector?.(".v-input--error, [aria-invalid='true']");
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    },

    // A blank email is allowed (partial saves); a filled one must be shaped
    // like an address, as the server requires.
    optionalEmail(value) {
      return !String(value || "").trim() || this.validateEmail(value);
    },

    // Runs the FORMAT rules (a bad ZIP, a malformed email, a blank display
    // name) and resolves true when the record may be saved. Blank launch-gate
    // fields do not fail it. Nothing is saved or logged here.
    async validate() {
      // Vuetify 3's validate() is async ({ valid }); a stub may return a bool.
      const form = this.$refs.form;
      let valid = true;
      if (form && typeof form.validate === "function") {
        const checked = await form.validate();
        valid = typeof checked === "boolean" ? checked : checked?.valid !== false;
      }
      if (!valid) {
        await this.$nextTick();
        this.scrollToFirstError();
        return false;
      }
      return true;
    },

    // Save a pending edit now (leaving the page). Resolves true when saved.
    flush() {
      return this.autosave.flush();
    },
  },
};
</script>

<style scoped>
.company-save-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
</style>
